import React, { useMemo, useState } from "react";
import { History } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import { useAlert } from "context/AlertContext";
import useServerList from "../../components/useServerList";
import { errorMessage } from "../../features/builder.utils";
import StatTiles from "../components/StatTiles";
import useStats from "../components/useStats";
import { durationText } from "../features/monitor.utils";
import apiReminderRunLog from "./api";
import { loadRunLogFilterDependencies } from "./features/run-log.dependencies";
import { runLogFilterFields } from "./features/run-log.filter";
import { getRunLogColumns } from "./run-log-list/runLogColumns";
import RunLogRowActions from "./run-log-list/RunLogRowActions";
import RunLogViewDrawer from "./run-log-dialogs/RunLogViewDrawer";

// Reminder Monitor -> Run log (tbl_rem_run_log): every evaluation of a rule (schedule, domain event, Run now, Test run).
const RunLog = () => {
  const { showAlert } = useAlert();
  const list = useServerList(apiReminderRunLog.getAll, { loadError: "Failed to load the run log.", initialSort: { key: "id", direction: "desc" } });
  const { stats, loading: statsLoading } = useStats(apiReminderRunLog.getStats, list.refreshKey);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);

  const openView = async (row) => {
    setBusy({ id: row.id });
    try {
      const res = await apiReminderRunLog.getById(row.id);
      setView(res.data?.data ?? null);
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const columns = useMemo(() => getRunLogColumns(list.start), [list.start]);
  const s = stats?.by_status ?? {};
  const slowest = [...(stats?.by_rule ?? [])].sort((a, b) => (b.avg_ms ?? 0) - (a.avg_ms ?? 0))[0];
  const tiles = [
    { label: "Successful runs", value: s.SUCCESS, tone: "success" },
    { label: "Partial", value: s.PARTIAL, tone: s.PARTIAL ? "warning" : "muted", hint: "Stopped at the scan limit" },
    { label: "Failed", value: s.FAILED, tone: s.FAILED ? "danger" : "muted" },
    { label: "Slowest rule (avg)", value: slowest ? durationText(slowest.avg_ms) : "—", hint: slowest?.name },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <StatTiles items={tiles} loading={statsLoading} note={stats ? `Runs started ${stats.from} to ${stats.to} (${stats.timezone}); test runs not counted.` : null} />
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          resetSearchTrigger={list.resetTrigger}
          canCreate={false}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={runLogFilterFields} dependenciesLoader={loadRunLogFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={History}
              title={list.hasQuery ? "No runs match" : "No runs yet"}
              message={list.hasQuery ? "No runs match your search or filters. Try adjusting them." : "Each time a rule runs on its schedule, on an event, by Run now or as a Test run, it is logged here."}
              showBack={false}
              showRefresh
              onRefresh={list.handleRefresh}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                selectable={false}
                showActions
                renderActions={(row) => <RunLogRowActions row={row} busy={busy} onView={openView} />}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={0} />
          </>
        )}
      </div>

      {view && <RunLogViewDrawer record={view} onClose={() => setView(null)} />}
    </div>
  );
};

export default RunLog;
